import KpiAPI from 'apis/KpiAPI';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useNotify from 'hooks/useNotify';
import React, { useEffect, useState } from 'react';
import Button from 'ui/elements/buttons/Button';
import { Content } from 'ui/views/dialogs/Dialog';
import useLazyResource from 'util/resource/useLazyResource';
import FormikTextField from 'ui/elements/form/formik/FormikTextField';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';
import useResource from 'util/resource/useResource';
import { getOrElse } from 'util/resource';
import { Kpi as KPI } from 'domain/kpis/types';
import { KPI_TEMPLATES, KpiTemplate } from 'pages/Company/kpis/kpiTemplates';
import Chip from 'ui/elements/Chip';
import styled from '@emotion/styled';
import { contentSpacing, halfSpacing, quarterSpacing, sectionSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';
import { fontSizeSmall, shape, typography } from 'ui/theme/themeBluePlanet';
import ChevronLeftIcon from 'ui/elements/icons/ChevronLeftIcon';
import CloseIcon from 'ui/elements/icons/CloseIcon';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { DisplayType, IndicatorType } from 'domain/kpis/types';
import { Select, MenuItem, FormControl, CircularProgress, alpha } from '@mui/material';
import Label from 'ui/elements/form/Label';

const TemplateGrid = styled.div<{ $isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${props => (props.$isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))')};
  gap: ${contentSpacing};
  margin-bottom: ${sectionSpacing};
`;

const TemplateCard = styled.div<{ $isSelected?: boolean; $isLoading?: boolean }>`
  cursor: ${props => (props.$isLoading ? 'wait' : 'pointer')};
  padding: ${contentSpacing};
  border: 1px solid;
  border-color: ${props =>
    props.$isSelected ? bluePlanetTheme.bluePlanetPalette.blue.main : bluePlanetTheme.bluePlanetPalette.grey.medium};
  border-radius: ${shape.borderRadius}px;
  transition: ${bluePlanetTheme.transitions.create(['all'])};
  background-color: ${props =>
    props.$isSelected ? bluePlanetTheme.bluePlanetPalette.blue.main : bluePlanetTheme.bluePlanetPalette.white};
  color: ${props =>
    props.$isSelected ? bluePlanetTheme.bluePlanetPalette.white : bluePlanetTheme.bluePlanetPalette.indigo.main};
  position: relative;
  opacity: ${props => (props.$isLoading ? 0.6 : 1)};
  pointer-events: ${props => (props.$isLoading ? 'none' : 'auto')};

  &:hover {
    border-color: ${props =>
      props.$isSelected ? bluePlanetTheme.bluePlanetPalette.blue.dark : bluePlanetTheme.bluePlanetPalette.grey.main};
    background-color: ${props =>
      props.$isSelected ? bluePlanetTheme.bluePlanetPalette.blue.dark : bluePlanetTheme.bluePlanetPalette.grey.light};
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TemplateTitle = styled.div`
  font-weight: ${typography.fontWeightMedium};
  font-size: ${typography.fontSize};
  margin-bottom: ${quarterSpacing};
`;

const TemplateDescription = styled.div<{ $isSelected?: boolean }>`
  font-size: ${fontSizeSmall};
  line-height: 1.5;
  color: ${props =>
    props.$isSelected ? bluePlanetTheme.bluePlanetPalette.white : bluePlanetTheme.bluePlanetPalette.indigo.main};
  margin-bottom: ${halfSpacing};
  opacity: ${props => (props.$isSelected ? 0.9 : 1)};
`;

const TemplateDetails = styled.div`
  display: flex;
  gap: ${halfSpacing};
  align-items: center;
  flex-wrap: wrap;
`;

const CloseButton = styled.button<{ $isLoading?: boolean }>`
  position: absolute;
  top: ${halfSpacing};
  right: ${halfSpacing};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${alpha(bluePlanetTheme.bluePlanetPalette.white, 0.2)};
  border: none;
  color: ${bluePlanetTheme.bluePlanetPalette.white};
  cursor: ${props => (props.$isLoading ? 'wait' : 'pointer')};
  padding: ${quarterSpacing};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  opacity: ${props => (props.$isLoading ? 1 : 1)};
  transition: ${bluePlanetTheme.transitions.create(['background'])};
  pointer-events: ${props => (props.$isLoading ? 'none' : 'auto')};

  &:hover {
    background: ${alpha(bluePlanetTheme.bluePlanetPalette.white, 0.3)};
  }
`;

const InlineForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${contentSpacing};
  padding: ${contentSpacing};
  background-color: ${bluePlanetTheme.bluePlanetPalette.white};
  border-radius: ${shape.borderRadius}px;
  border: 1px solid ${bluePlanetTheme.bluePlanetPalette.grey.medium};
  margin-bottom: ${sectionSpacing};
`;

const FormRow = styled.div<{ $isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${props => (props.$isMobile ? '1fr' : '2fr 1fr 1fr')};
  gap: ${contentSpacing};
  align-items: end;
`;

const getDisplayTypeLabel = (displayType: DisplayType): string => {
  switch (displayType) {
    case 'LineChart':
      return 'Line Chart';
    case 'BarChart':
      return 'Bar Chart';
    case 'NumberIndicator':
      return 'Number';
  }
};

export default function KpisForm({
  onStart,
  onKpiUploaded,
  companyId,
}: {
  onStart?: () => void;
  onKpiUploaded: () => void;
  companyId: number;
}) {
  useEffect(() => {
    onStart?.();
  }, [onStart]);

  const notify = useNotify();
  const { setLastUpdated } = useCompanyLastUpdated(companyId);
  const { isMobile } = useBreakpoints();
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);
  const [loadingKpiName, setLoadingKpiName] = useState<string | null>(null);
  const [removingKpiId, setRemovingKpiId] = useState<string | null>(null);

  const { resource, mutate } = useResource<KPI[]>(KpiAPI.kpi.list(companyId));
  const existingKPIs = getOrElse(resource, []).filter(kpi => !kpi.details.archivedAt);

  const [addKpi, isAddingKpi] = useLazyResource((payload: any) => KpiAPI.kpi.new(companyId, payload), {
    onSuccess: () => {
      onKpiUploaded();
      mutate(); // Refresh from server
      setShowTemplates(true);
      setShowCustomForm(false);
      setLoadingKpiName(null);
      formik.resetForm();
    },
    onFailure: () => {
      notify('error', `Something unexpected happened when trying to add the KPI.`);
      setLoadingKpiName(null);
    },
  });

  const [archiveKpi] = useLazyResource((kpiId: string) => KpiAPI.kpi.archive(companyId, kpiId), {
    onSuccess: (_, kpiId) => {
      mutate(existingKPIs.filter(it => it.details.id !== kpiId));
      setLastUpdated(new Date());
      setRemovingKpiId(null);
    },
    onFailure: () => {
      notify('error', `Something unexpected happened when trying to remove the KPI.`);
      setRemovingKpiId(null);
    },
  });

  const handleTemplateClick = (template: KpiTemplate) => {
    // Check if already selected
    const existing = existingKPIs.find(it => it.details.name === template.name);

    if (existing) {
      // Remove if already selected
      setRemovingKpiId(existing.details.id);
      archiveKpi(existing.details.id);
    } else {
      // Set loading state
      setLoadingKpiName(template.name);

      // Add KPI with full configuration
      addKpi({
        name: template.name,
        unitOfMeasure: template.unitOfMeasure,
        initialDisplays: [
          {
            displayType: template.displayType,
            trendIndicator: template.trendIndicator,
            showXAxis: template.showXAxis,
            showYAxis: template.showYAxis,
            comparisonEnabled: template.comparisonEnabled,
            comparisonPeriod: template.comparisonPeriod,
            visibility: template.visibility,
            ...(template.displayType === 'BarChart' && { maxDataPoints: 12 }),
          },
        ],
      });
    }
  };

  const isTemplateSelected = (templateName: string) => {
    return existingKPIs.some(kpi => kpi.details.name === templateName);
  };

  const formik = useFormik({
    initialValues: {
      kpiName: '',
      displayType: 'LineChart' as DisplayType,
      unitOfMeasure: '',
    },
    onSubmit: values => {
      if (existingKPIs.some(it => it.details.name === values.kpiName)) {
        notify('error', `A KPI named "${values.kpiName}" already exists`);
        return;
      }

      // Set loading state
      setLoadingKpiName(values.kpiName);

      // Add custom KPI
      addKpi({
        name: values.kpiName,
        unitOfMeasure: values.unitOfMeasure || undefined,
        initialDisplays: [
          {
            displayType: values.displayType,
            trendIndicator: 'Up' as IndicatorType,
            showXAxis: values.displayType !== 'NumberIndicator',
            showYAxis: values.displayType !== 'NumberIndicator',
            comparisonEnabled: true,
            comparisonPeriod: values.displayType === 'BarChart' ? 'Year' : 'Month',
            ...(values.displayType === 'BarChart' && { maxDataPoints: 12 }),
          },
        ],
      });
    },
    validationSchema: Yup.object().shape({
      kpiName: Yup.string().trim().required('You need to add a short descriptive name of the KPI'),
      displayType: Yup.string().required(),
      unitOfMeasure: Yup.string(),
    }),
  });

  // Find custom KPIs (not in templates)
  const customKPIs = existingKPIs.filter(kpi => !KPI_TEMPLATES.some(template => template.name === kpi.details.name));

  return (
    <Content>
      {/* Template and custom KPI grid */}
      {showTemplates && !showCustomForm && (
        <>
          <div className="u-content-spacing-bottom">
            <h4 className="text-h4 u-half-spacing-bottom">Choose from templates</h4>
            <p className="text-body">Select a common KPI to get started quickly, or create your own custom KPI.</p>
          </div>
          <TemplateGrid $isMobile={isMobile}>
            {/* Custom KPIs as cards - shown first */}
            {customKPIs.map(kpi => {
              const display = kpi.displays?.[0];
              const isRemoving = removingKpiId === kpi.details.id;

              return (
                <TemplateCard key={kpi.details.id} $isSelected={true} onClick={() => {}}>
                  <CloseButton
                    $isLoading={isRemoving}
                    onClick={e => {
                      e.stopPropagation();
                      setRemovingKpiId(kpi.details.id);
                      archiveKpi(kpi.details.id);
                    }}
                    type="button"
                  >
                    {isRemoving ? (
                      <CircularProgress size={12} sx={{ color: bluePlanetTheme.bluePlanetPalette.white }} />
                    ) : (
                      <CloseIcon fontSize="small" />
                    )}
                  </CloseButton>
                  <TemplateTitle>{kpi.details.name}</TemplateTitle>
                  <TemplateDescription $isSelected={true}>Custom KPI</TemplateDescription>
                  <TemplateDetails>
                    {kpi.details.unitOfMeasure && (
                      <Chip color="white" label={kpi.details.unitOfMeasure} fontSize="small" />
                    )}
                    {display && (
                      <Chip color="white" label={getDisplayTypeLabel(display.displayType)} fontSize="small" />
                    )}
                  </TemplateDetails>
                </TemplateCard>
              );
            })}

            {/* Template KPIs - shown after custom KPIs */}
            {KPI_TEMPLATES.map(template => {
              const isSelected = isTemplateSelected(template.name);
              const isLoading = loadingKpiName === template.name;
              const kpi = existingKPIs.find(k => k.details.name === template.name);
              const isRemoving = kpi && removingKpiId === kpi.details.id;

              return (
                <TemplateCard
                  key={template.name}
                  $isSelected={isSelected}
                  $isLoading={isLoading}
                  onClick={() => handleTemplateClick(template)}
                >
                  {isLoading && (
                    <LoadingOverlay>
                      <CircularProgress size={24} />
                    </LoadingOverlay>
                  )}
                  {isSelected && !isLoading && (
                    <CloseButton
                      $isLoading={isRemoving}
                      onClick={e => {
                        e.stopPropagation();
                        if (kpi) {
                          setRemovingKpiId(kpi.details.id);
                          archiveKpi(kpi.details.id);
                        }
                      }}
                      type="button"
                    >
                      {isRemoving ? (
                        <CircularProgress size={12} sx={{ color: bluePlanetTheme.bluePlanetPalette.white }} />
                      ) : (
                        <CloseIcon fontSize="small" />
                      )}
                    </CloseButton>
                  )}
                  <TemplateTitle>{template.name}</TemplateTitle>
                  {template.description && (
                    <TemplateDescription $isSelected={isSelected}>{template.description}</TemplateDescription>
                  )}
                  <TemplateDetails>
                    {template.unitOfMeasure && (
                      <Chip
                        color={isSelected ? 'white' : 'grey-light'}
                        label={template.unitOfMeasure}
                        fontSize="small"
                      />
                    )}
                    <Chip
                      color={isSelected ? 'white' : 'blue-light'}
                      label={getDisplayTypeLabel(template.displayType)}
                      fontSize="small"
                    />
                  </TemplateDetails>
                </TemplateCard>
              );
            })}
          </TemplateGrid>

          <div className="u-content-spacing-top">
            <Button kind="tertiary" onClick={() => setShowCustomForm(true)}>
              Add your own
            </Button>
          </div>
        </>
      )}

      {/* Inline custom form */}
      {showCustomForm && (
        <>
          <div className="u-half-spacing-bottom">
            <Button
              kind="tertiary"
              size="small"
              onClick={() => {
                setShowCustomForm(false);
                formik.resetForm();
              }}
            >
              <ChevronLeftIcon fontSize="small" className="u-quarter-spacing-right" />
              Back to templates
            </Button>
          </div>

          <InlineForm>
            <FormRow $isMobile={isMobile}>
              <FormikTextField
                name="kpiName"
                formikProps={formik}
                autoFocus
                placeholder="e.g. Revenue, Customers..."
                label="Name"
                required
              />

              <div>
                <Label>Chart Type</Label>
                <FormControl fullWidth size="small">
                  <Select
                    value={formik.values.displayType}
                    onChange={e => formik.setFieldValue('displayType', e.target.value)}
                  >
                    <MenuItem value="LineChart">Line</MenuItem>
                    <MenuItem value="BarChart">Bar</MenuItem>
                    <MenuItem value="NumberIndicator">Number</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <FormikTextField name="unitOfMeasure" formikProps={formik} placeholder="$, %, etc." label="Unit" />
            </FormRow>

            <div>
              <Button kind="primary" onClick={() => formik.submitForm()} isLoading={isAddingKpi}>
                Add KPI
              </Button>
            </div>
          </InlineForm>
        </>
      )}
    </Content>
  );
}
